import type { GridWidgetType } from "./types";
import type { WidgetDefinition } from "./widgetDefinition";
import type { ComponentSpec, ProcessedWidgetData } from "./Processing/types";

import { chartDefinition } from "./Definitions/chart";
import { tableDefinition } from "./Definitions/table";
import { table2dDefinition } from "./Definitions/table2d";
import { titleDefinition } from "./Definitions/title";
import { textDefinition } from "./Definitions/text";
import { pieDefinition } from "./Definitions/pie";
import { logoDefinition } from "./Definitions/logo";
import { statDefinition } from "./Definitions/stat";
import { spacerDefinition } from "./Definitions/spacer";
import { bodyMapDefinition } from "./Definitions/bodyMap";
import { gaugeDefinition } from "./Definitions/gauge";
import { radarDefinition } from "./Definitions/radar";
import { playerCardDefinition } from "./Definitions/playerCard";
import { playerNotesDefinition } from "./Definitions/playerNotes";
import { aiInsightsDefinition } from "./Definitions/aiInsights";
import { dynamicControllerDefinition } from "./Definitions/dynamicController";
import { scatterplotDefinition } from "./Definitions/scatterplot";

/**
 * All registered widget definitions, keyed by type.
 *
 * Typing this as `Record<GridWidgetType, WidgetDefinition>` makes the compiler
 * enforce that EVERY widget type has exactly one definition — since
 * `GridWidgetType = ComponentSpec["type"]`, adding a new widget spec produces a
 * compile error here until its definition is registered. To add a new widget
 * type, create a definition file in definitions/ and add it to this record.
 * (Object insertion order is preserved by `Object.values`, so it also drives the
 * TemplatePicker order.)
 */
const DEFINITIONS_BY_TYPE: Record<GridWidgetType, WidgetDefinition> = {
  chart: chartDefinition,
  table: tableDefinition,
  table_2d: table2dDefinition,
  title: titleDefinition,
  text: textDefinition,
  pie_chart: pieDefinition,
  logo: logoDefinition,
  spacer: spacerDefinition,
  body_map: bodyMapDefinition,
  gauge: gaugeDefinition,
  radar: radarDefinition,
  player_card: playerCardDefinition,
  stat_tile: statDefinition,
  player_notes: playerNotesDefinition,
  ai_insights: aiInsightsDefinition,
  slicer: dynamicControllerDefinition,
  scatterplot: scatterplotDefinition,
};

const ALL_DEFINITIONS: WidgetDefinition[] = Object.values(DEFINITIONS_BY_TYPE);

const registry = new Map<GridWidgetType, WidgetDefinition>(
  ALL_DEFINITIONS.map((d) => [d.type, d]),
);

/** Get a widget definition by type. */
export function getWidgetDef(type: GridWidgetType): WidgetDefinition | undefined {
  return registry.get(type);
}

/** Get all registered definitions. */
export function getAllWidgetDefs(): WidgetDefinition[] {
  return ALL_DEFINITIONS;
}

/** Get template entries for the TemplatePicker. */
export function getWidgetTemplates() {
  return ALL_DEFINITIONS
    .filter((d) => d.template)
    .map((d) => ({
      type: d.type,
      label: d.template.label,
      description: d.template.description,
      icon: d.template.icon,
    }));
}

// ── Compile-time drift guards ──────────────────────────────────
// These produce no runtime code; they fail the build if the widget-type unions
// fall out of sync. `DEFINITIONS_BY_TYPE` above already forces the registry to
// cover every `GridWidgetType` (= `ComponentSpec["type"]`). This additionally
// asserts the processing union stays 1:1 with the spec union, so adding a new
// spec type without a matching `ProcessedWidgetData` arm is a compile error.
type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends (<T>() => T extends B ? 1 : 2) ? true : false;
type Expect<T extends true> = T;
type _ProcessedCoversSpecs = Expect<Equals<ComponentSpec["type"], ProcessedWidgetData["type"]>>;
